import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import StarIcon from '../../../assets/star-icon.svg';
import { PATHS } from '../../../constants/Paths.constants';
import { useAppDispatch, useAppSelector } from '../../../hooks/redux';
import { useOwners } from '../../../hooks/useOwners';
import {
  continueAiSystemDraft,
  fetchAISystems,
  fetchOrganizations,
  startAiSystem
} from '../../../services/AISystemsService';
import { getAIClassifications } from '../../../services/AIClassificationService';
import {
  appendSystemsSuccess,
  fetchSystemsFailure,
  fetchSystemsStart,
  fetchSystemsSuccess,
  resetFilters,
  setComplianceFilter,
  setEntityFilter,
  setOffset,
  setOrganizationFilter,
  setOwnersFilter,
  setRiskFilter,
  setSearch,
  setSorting
} from '../../../store/slices/aiSystemsSlice';
import {
  setAiSystemId,
  setEnableStepper,
  setIsDraftLoaded,
  setPreStepPart,
  setPreviousQuestions,
  setQuestionnaireId,
  setShowPreStep,
  setStartAiSystemBodyResponse,
  setStartAiSystemBodyRequest,
  updateStepBasedOnBackend
} from '../../../store/slices/stepperSlice';
import {
  fetchClassificationsFailure,
  fetchClassificationsStart,
  fetchClassificationsSuccess,
  resetAIPowered
} from '../../../store/slices/aiPoweredSlice';
import { fetchSummaryData } from '../../../store/slices/summarySlice';
import { Owner, StartAiSystemRequest } from '../../../types/AISystems.types';
import { isAiSystemDraftStatus } from '../../../utils/AiSystems.util';
import { mapSummaryToStatsSection } from '../../../utils/SummaryMapping.util';
import { AISystemsTable } from '../../tables/AISystemsTable/AISystemsTable';
import { EmptyState } from '../../ui/EmptyState/EmptyState';
import Loader from '../../ui/Loader/Loader';
import { FiltersSection } from '../FilterSection/FiltersSection';
import { SearchSection } from '../SearchSection/SearchSection';
import { StatsSection } from '../StatsSection/StatsSection';
import { setReportData } from '../../../store/slices/reportSlice';
import {
  initialDocumentationParams,
  setDocumentationReqBody,
  setDocuments
} from '../../../store/slices/documentationSlice';
import { resetSystemLogging } from '../../../store/slices/systemLoggingSlice';
import { resetRepository } from '../../../store/slices/repositorySlice';
import { AddAISystemAction } from '../AddAISystemAction/AddAISystemAction';

export function AISystemsOverview() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const { systems, total, loading, filters, pagination, sorting } =
    useAppSelector((state) => state.aiSystems);
  const { data: summaryData } = useAppSelector((state) => state.summary);
  const { user } = useAppSelector((state) => state.auth);

  const { owners, loading: ownersLoading } = useOwners();

  const [selectedSystemId, setSelectedSystemId] = useState<string | null>(null);
  const [organizationOptions, setOrganizationOptions] = useState<
    { value: string; label: string }[]
  >([]);
  const [organizationsLoading, setOrganizationsLoading] = useState(false);
  const hasMore = systems.length < total;

  const ownerOptions = useMemo(() => {
    const options = owners.map((owner: Owner) => ({
      value: owner.id,
      label:
        user?.email === owner.email
          ? 'me'
          : `${owner.firstName} ${owner.lastName}`
    }));

    // set 'me' as first option
    const meIndex = options.findIndex((option) => option.label === 'me');
    if (meIndex > 0) {
      const [meOption] = options.splice(meIndex, 1);
      options.unshift(meOption);
    }

    return options;
  }, [owners, user?.email]);

  useEffect(() => {
    dispatch(fetchSummaryData());
  }, [dispatch]);

  useEffect(() => {
    const loadOrganizations = async () => {
      setOrganizationsLoading(true);
      try {
        const orgs = await fetchOrganizations();
        setOrganizationOptions(orgs.map((org) => ({ value: org, label: org })));
      } catch (error) {
        console.error('Failed to fetch organizations:', error);
        setOrganizationOptions([]);
      } finally {
        setOrganizationsLoading(false);
      }
    };

    loadOrganizations();
  }, []);

  useEffect(() => {
    dispatch(setEnableStepper(false));
    dispatch(setDocumentationReqBody(initialDocumentationParams));
    dispatch(setDocuments({ items: [], total: 0 }));
    dispatch(resetSystemLogging());
    dispatch(resetRepository());
    dispatch(resetAIPowered());
  }, [dispatch]);

  useEffect(() => {
    if (!user?.id) return;

    let isActive = true;

    const fetchSystems = async () => {
      dispatch(fetchSystemsStart());
      try {
        const response = await fetchAISystems({
          search: filters.search,
          entity: filters.entity,
          riskClassification: filters.riskClassification,
          complianceStatus: filters.complianceStatus,
          ownerId: filters.owners,
          organization: filters.organization,
          offset: pagination.offset,
          limit: pagination.limit,
          sortBy: sorting.sortBy,
          order: sorting.order
        });
        if (!isActive) return;

        if (pagination.offset === 0) {
          dispatch(fetchSystemsSuccess(response));
        } else {
          dispatch(appendSystemsSuccess(response));
        }
      } catch (error: any) {
        if (!isActive) return;
        dispatch(fetchSystemsFailure(error.message));
      }
    };

    fetchSystems();

    return () => {
      isActive = false;
    };
  }, [
    dispatch,
    filters,
    pagination.limit,
    pagination.offset,
    sorting,
    user?.id
  ]);

  const hasActiveFilters = useMemo(() => {
    return (
      (filters.entity && filters.entity.length > 0) ||
      (filters.complianceStatus && filters.complianceStatus.length > 0) ||
      (filters.riskClassification && filters.riskClassification.length > 0) ||
      (filters.owners && filters.owners.length > 0) ||
      (filters.organization && filters.organization.length > 0) ||
      (filters.search && filters.search.length > 0)
    );
  }, [filters]);

  const statsData = useMemo(() => {
    if (summaryData) {
      return mapSummaryToStatsSection(summaryData);
    }
    return {
      totalSystems: 0,
      complianceData: {
        compliant: 0,
        inProgress: 0,
        nonCompliant: 0
      },
      riskData: {
        minimal: 0,
        limited: 0,
        high: 0,
        prohibited: 0,
        other: 0
      }
    };
  }, [summaryData]);

  const handleSearchChange = (value: string) => {
    dispatch(setSearch(value));
  };

  const handleResetFilters = () => {
    dispatch(resetFilters());
  };

  const handleLoadMore = () => {
    if (!loading && hasMore) {
      dispatch(setOffset(systems.length));
    }
  };

  const handleDelete = () => {
    const fetchSystems = async () => {
      dispatch(fetchSystemsStart());
      try {
        const response = await fetchAISystems({
          search: filters.search,
          entity: filters.entity,
          riskClassification: filters.riskClassification,
          complianceStatus: filters.complianceStatus,
          ownerId: filters.owners,
          organization: filters.organization,
          offset: 0,
          limit: pagination.limit,
          sortBy: sorting.sortBy,
          order: sorting.order
        });
        dispatch(fetchSystemsSuccess(response));
        dispatch(fetchSummaryData());
      } catch (error: any) {
        dispatch(fetchSystemsFailure(error.message));
      }
    };

    fetchSystems();
  };

  const handleSort = (sortKey: string) => {
    if (sorting.sortBy === sortKey) {
      const newOrder = sorting.order === 'asc' ? 'desc' : 'asc';
      dispatch(setSorting({ sortBy: sortKey, order: newOrder }));
    } else {
      dispatch(setSorting({ sortBy: sortKey, order: 'asc' }));
    }
  };

  const handleSystemSelect = async (id: string) => {
    // Find the selected system from the list and set it as current
    const selectedSystem = systems.find((system) => system.id === id);

    if (!selectedSystem) return;

    if (
      selectedSystem.origin === 'BULK_IMPORT' &&
      selectedSystem.complianceStatus === 'INITIATED'
    ) {
      const basicInformation = {
        name: selectedSystem.name,
        version: selectedSystem.version,
        description: selectedSystem.description ?? '',
        organization: selectedSystem.organization ?? '',
        complianceContact: selectedSystem.complianceContact ?? ''
      };

      const hasEmptyValue = Object.values(basicInformation).some(
        (value) =>
          value === null ||
          value === undefined ||
          (typeof value === 'string' && value.trim() === '')
      );

      if (hasEmptyValue) {
        dispatch(setAiSystemId(selectedSystem.id));
        dispatch(setEnableStepper(true));
        dispatch(setShowPreStep(true));
        dispatch(setPreStepPart('basicInformation'));
        navigate(PATHS.QUESTIONNAIRE, { state: { basicInformation } });
      } else {
        const startAiSystemRequest: StartAiSystemRequest = {
          ...basicInformation,
          assessmentType: 'QUESTIONNAIRE',
          aiSystemId: selectedSystem.id
        };

        dispatch(setStartAiSystemBodyRequest(startAiSystemRequest));

        const response = await startAiSystem(startAiSystemRequest);

        dispatch(setStartAiSystemBodyResponse(response));
        dispatch(setEnableStepper(true));
        dispatch(setShowPreStep(true));
        dispatch(setPreStepPart('entityClassification'));
        navigate(PATHS.QUESTIONNAIRE);
      }
      return;
    }

    if (
      isAiSystemDraftStatus(selectedSystem.complianceStatus) &&
      selectedSystem.assessmentType === 'AI_POWERED'
    ) {
      dispatch(resetAIPowered());
      dispatch(setIsDraftLoaded(true));
      dispatch(setEnableStepper(true));
      dispatch(setShowPreStep(false));
      dispatch(setAiSystemId(id));
      dispatch(
        setStartAiSystemBodyRequest({
          name: selectedSystem.name,
          version: selectedSystem.version,
          description: selectedSystem.description ?? '',
          organization: selectedSystem.organization ?? '',
          complianceContact: selectedSystem.complianceContact ?? '',
          assessmentType: 'AI_POWERED'
        })
      );

      try {
        dispatch(fetchClassificationsStart());
        const classifications = await getAIClassifications(id, {
          limit: 1,
          offset: 0
        });
        dispatch(fetchClassificationsSuccess(classifications));
        navigate(`/questionnaire`);
      } catch (error: any) {
        dispatch(
          fetchClassificationsFailure(
            error instanceof Error
              ? error.message
              : 'Failed to get AI classifications'
          )
        );
      }

      return;
    }

    if (isAiSystemDraftStatus(selectedSystem.complianceStatus)) {
      try {
        const response = await continueAiSystemDraft(id);
        const questions = Array.isArray(response.questions)
          ? response.questions
          : response.questions
            ? [response.questions]
            : [];
        const currentQuestion = response.question || questions[0];
        const isEntityResumeQuestion =
          currentQuestion?.section === 'Entity' || currentQuestion?.id === 'E1';

        dispatch(setIsDraftLoaded(true));
        dispatch(setEnableStepper(true));
        dispatch(setQuestionnaireId(response.questionnaireId));
        dispatch(setAiSystemId(id));
        dispatch(updateStepBasedOnBackend(response.step));

        if (response.step === 'REPORT') {
          dispatch(setShowPreStep(false));
          dispatch(setStartAiSystemBodyResponse(null));
          dispatch(setReportData(response));
        } else if (isEntityResumeQuestion && currentQuestion) {
          dispatch(setShowPreStep(true));
          dispatch(setPreStepPart('entityClassification'));
          dispatch(setPreviousQuestions([]));
          dispatch(
            setStartAiSystemBodyResponse({
              ...response,
              aiSystemId: response.aiSystemId || id,
              question: currentQuestion
            })
          );
        } else {
          dispatch(setShowPreStep(false));
          dispatch(setStartAiSystemBodyResponse(null));
          dispatch(setPreviousQuestions(questions));
        }

        navigate(`/questionnaire`);
      } catch (error) {
        throw new Error('Failed to fetch ai system');
      }
    } else {
      dispatch({
        type: 'aiSystems/fetchCurrentSystemSuccess',
        payload: selectedSystem
      });
      navigate(`/ai-systems/${id}`);
    }
  };

  return (
    <div className="flex flex-col gap-4 w-full h-full">
      {!loading && systems.length === 0 && !hasActiveFilters && (
        <EmptyState
          icon={<img src={StarIcon} alt="AI Systems" className="w-12 h-12" />}
          title="No AI systems yet"
          description="Get started by adding your first AI system for compliance assessment."
          action={<AddAISystemAction menuAlign="center" />}
        />
      )}

      {loading && systems.length === 0 && !hasActiveFilters && (
        <div className="absolute top-1/2 left-1/2">
          <Loader />
        </div>
      )}

      {(systems.length > 0 || hasActiveFilters) && (
        <>
          <StatsSection
            totalSystems={statsData.totalSystems}
            complianceData={statsData.complianceData}
            riskData={statsData.riskData}
          />
          <SearchSection
            searchValue={filters.search}
            onSearchChange={handleSearchChange}
            placeholder="Search by keyword"
            inputClassName="border-gray-200"
          />
          <FiltersSection
            selectedEntity={filters.entity || []}
            onEntityChange={(values: string[]) =>
              dispatch(setEntityFilter(values))
            }
            selectedComplianceStatus={filters.complianceStatus || []}
            onComplianceStatusChange={(values: string[]) =>
              dispatch(setComplianceFilter(values))
            }
            selectedRiskClassification={filters.riskClassification || []}
            onRiskClassificationChange={(values: string[]) =>
              dispatch(setRiskFilter(values))
            }
            selectedOwners={filters.owners || []}
            onOwnersChange={(values: string[]) =>
              dispatch(setOwnersFilter(values))
            }
            selectedOrganization={filters.organization || []}
            onOrganizationChange={(values: string[]) =>
              dispatch(setOrganizationFilter(values))
            }
            onResetFilters={handleResetFilters}
            ownerOptions={ownerOptions}
            ownersLoading={ownersLoading}
            organizationOptions={organizationOptions}
            organizationsLoading={organizationsLoading}
          />
          <div className="flex-1 w-full relative">
            <AISystemsTable
              aiSystems={systems}
              selectedId={selectedSystemId}
              onSelect={handleSystemSelect}
              loading={loading}
              hasMore={hasMore}
              onLoadMore={handleLoadMore}
              onDelete={handleDelete}
              sortBy={sorting.sortBy}
              sortOrder={sorting.order}
              onSort={handleSort}
            />
          </div>
        </>
      )}
    </div>
  );
}
